import type { ReactNode } from "react";
import { useDokument, useSammlung, type Zeile } from "../plattform/lesemodelle";
import { useSitzung } from "../plattform/sitzung";
import { Symbol } from "../design/Symbol";

/** Gemeinsame Teile der Ansichten einer Drückjagd (lesemodelle.md §3, §4). */

export interface Person {
  Kennung: string;
  Name: string;
  Anrede: string;
}

export function name(p: unknown): string {
  return p && typeof p === "object" && "Name" in p ? String((p as Person).Name) : "—";
}

export function namen(liste: unknown): string {
  return Array.isArray(liste) && liste.length ? liste.map(name).join(", ") : "—";
}

export function wert(w: unknown): string {
  if (w === null || w === undefined || w === "") return "—";
  if (typeof w === "boolean") return w ? "ja" : "nein";
  if (Array.isArray(w)) return w.length ? w.map(wert).join(", ") : "—";
  if (w instanceof Date) return `${w.toLocaleDateString("de-DE")} ${w.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`;
  return String(w);
}

/** Kopf der Drückjagd für die angemeldete Person (Bezeichnung, Jagddatum, Status). */
export function useKopf(id: string) {
  const { uid } = useSitzung();
  return useDokument(uid ? `drueckjagden/${id}/kopf/${uid}` : null);
}

export function useMeineAufgaben(id: string) {
  const { uid } = useSitzung();
  return useDokument(uid ? `drueckjagden/${id}/meine_aufgaben/${uid}` : null);
}

/** Revier der angemeldeten Person in dieser Drückjagd (Einteilung bzw. Jagdherr), aus Meine Aufgaben. */
export function useRevier(id: string): string | null {
  const a = useMeineAufgaben(id);
  return ((a.daten?.["_kennungen"] as Record<string, string> | undefined)?.["Revier"]) ?? null;
}

/**
 * Rollen der angemeldeten Person in dieser Drückjagd, aus Meine Drückjagden (`Rollen`, lesemodelle.md §6): `Jagdherr`,
 * wenn sie Jagdherr des Reviers ist, in dem sie mitwirkt, dazu ihre Rollen in dessen Einteilung. Bis das Dokument
 * gelesen ist, keine.
 */
export function useEigeneRollen(id: string): readonly string[] {
  const { uid } = useSitzung();
  const eintrag = useDokument(uid ? `personen/${uid}/meine_drueckjagden/${id}` : null);
  return (eintrag.daten?.["Rollen"] as string[] | undefined) ?? [];
}

/**
 * Ob die angemeldete Person Jagdherr des Veranstalters ist (Jagdzeitplan festlegen, Reviere beteiligen; befehle.md §6.3):
 * Jagdherr des Reviers, in dem sie mitwirkt, und dieses Revier ist der Veranstalter (`_revier` des Eintrags in Meine
 * Drückjagden, lesemodelle.md §1.2). Bis der Eintrag gelesen ist, nein. Dazu die RevierId des Veranstalters.
 */
export function useVeranstalter(id: string): { veranstalter: string | null; jagdherrDesVeranstalters: boolean } {
  const { uid } = useSitzung();
  const eintrag = useDokument(uid ? `personen/${uid}/meine_drueckjagden/${id}` : null).daten;
  const veranstalter = (eintrag?.["_revier"] as string | undefined) ?? null;
  const eigenes = (eintrag?.["_kennungen"] as Record<string, string> | undefined)?.["Revier"] ?? null;
  const rollen = (eintrag?.["Rollen"] as string[] | undefined) ?? [];
  return { veranstalter, jagdherrDesVeranstalters: !!veranstalter && veranstalter === eigenes && rollen.includes("Jagdherr") };
}

export function useRevierSammlung(id: string, revier: string | null, sammlung: string) {
  return useSammlung(revier ? `drueckjagden/${id}/reviere/${revier}/${sammlung}` : null);
}

/**
 * Bereich einer Liste (design/README §9.8; Referenz `liste-bereich`, `text-bereich`): `region` mit dem Namen der Gruppe,
 * Überschrift als Gruppenkopf mit Symbol (oberflaeche.md §2.2). `platte`: Text liegt auf der ruhigen Platte; ohne Platte
 * stehen Einträge mit eigener Fläche darin (Kacheln, Einträge).
 */
export function Abschnitt({ name: titel, symbol, platte = true, text, children }: {
  name: string; symbol?: string; platte?: boolean; text?: boolean; children: ReactNode;
}) {
  return (
    <section className={`${text ? "text-bereich" : "liste-bereich"}${platte ? " platte" : ""}`} role="region" aria-label={titel}>
      {text
        // Textbereich wie die Referenz (`text-bereich`, `#s-einweisung`): Überschrift ohne Symbol, das lange Wort
        // („Sicherheitsbestimmungen“) braucht bei 200 % die ganze Breite
        ? <h2 className="kopf">{titel}</h2>
        : <h2 className={`kopf gruppenkopf${platte ? "" : " platte"}`}>{symbol ? <Symbol name={symbol} /> : null}{titel}</h2>}
      {children}
    </section>
  );
}

export function Leer({ text, symbol = "info" }: { text: string; symbol?: string }) {
  return <p className="leer platte"><Symbol name={symbol} /><span>{text}</span></p>;
}

export function Angaben({ zeilen }: { zeilen: Array<[string, ReactNode]> }) {
  return (
    <dl className="angaben platte">
      {zeilen.map(([k, v]) => (
        <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
      ))}
    </dl>
  );
}

export type { Zeile };
